import { useEffect, useState } from "react";
import { CalendarClock, Trash2, Save, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, PageTitle, Stat } from "@/components/Common";
import { HorarioSemanal, DIAS, sumarMinutos } from "@/components/HorarioSemanal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminHorarios() {
  const [bloques, setBloques] = useState(null);
  const [materias, setMaterias] = useState([]);
  const [grado, setGrado] = useState("");
  const [sabado, setSabado] = useState(false);
  const [form, setForm] = useState(null);

  const cargar = () => {
    api.get("/horarios").then((r) => setBloques(r.data)).catch(() => setBloques([]));
    api.get("/materias").then((r) => setMaterias(r.data));
  };

  useEffect(cargar, []);

  useEffect(() => {
    if (!grado && materias.length) setGrado(materias[0].grado);
  }, [materias, grado]);

  const guardar = async () => {
    if (!form.materia_id) return toast.error("Seleccione la materia");
    if (form.hora_fin <= form.hora_inicio) return toast.error("La hora final debe ser mayor que la inicial");
    const datos = {
      materia_id: form.materia_id, dia: Number(form.dia),
      hora_inicio: form.hora_inicio, hora_fin: form.hora_fin, aula: form.aula || null,
    };
    try {
      if (form.id) await api.put(`/horarios/${form.id}`, datos);
      else await api.post("/horarios", datos);
      toast.success(form.id ? "Bloque actualizado" : "Clase agregada al horario");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async () => {
    try {
      await api.delete(`/horarios/${form.id}`);
      toast.success("Bloque eliminado del horario");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!bloques) return <Loading />;

  const grados = [...new Set(materias.map((m) => m.grado))];
  const visibles = (grado ? bloques.filter((b) => b.grado === grado) : bloques)
    .map((b) => ({ ...b, subtitulo: [b.grado, b.aula].filter(Boolean).join(" · ") }));
  const materiasGrado = grado ? materias.filter((m) => m.grado === grado) : materias;
  const sinHorario = materiasGrado.filter((m) => !bloques.some((b) => b.materia_id === m.id));
  const horas = visibles.reduce((t, b) => {
    const min = (h) => Number(h.split(":")[0]) * 60 + Number(h.split(":")[1]);
    return t + (min(b.hora_fin) - min(b.hora_inicio)) / 60;
  }, 0);

  const abrirSlot = (dia, hora) => setForm({
    materia_id: materiasGrado[0]?.id || "", dia, hora_inicio: hora,
    hora_fin: sumarMinutos(hora, 60), aula: "",
  });

  return (
    <div data-testid="admin-horarios">
      <PageTitle title="Horario de clases"
                 subtitle="Toque cualquier espacio del calendario para agregar una clase, o un bloque para editarlo.">
        <Button className="rounded-full" data-testid="nuevo-bloque"
                onClick={() => abrirSlot(0, "08:00")} disabled={materiasGrado.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> Agregar clase
        </Button>
      </PageTitle>

      <div className="grid gap-4 sm:grid-cols-3" data-testid="horario-stats">
        <Stat label="Bloques" value={String(visibles.length)} icon={CalendarClock} />
        <Stat label="Horas semanales" value={`${horas.toFixed(1)} h`} />
        <Stat label="Materias sin horario" value={String(sinHorario.length)} />
      </div>

      <div className="mt-8 flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <Label>Grado</Label>
          <select value={grado} onChange={(e) => setGrado(e.target.value)} data-testid="filtro-grado"
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm">
            <option value="">Todos los grados</option>
            {grados.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={sabado} data-testid="ver-sabado"
                 onChange={(e) => setSabado(e.target.checked)} />
          Mostrar sábado
        </label>
      </div>

      {sinHorario.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="materias-sin-horario">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Sin horario:</span>
          {sinHorario.map((m) => (
            <Badge key={m.id} variant="outline" className="cursor-pointer"
                   onClick={() => setForm({ materia_id: m.id, dia: 0, hora_inicio: "08:00", hora_fin: "09:00", aula: "" })}>
              {m.nombre}
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-6">
        <HorarioSemanal bloques={visibles} dias={sabado ? 6 : 5}
                        onSlot={materiasGrado.length ? abrirSlot : undefined}
                        onBloque={(b) => setForm({
                          id: b.id, materia_id: b.materia_id, dia: b.dia,
                          hora_inicio: b.hora_inicio, hora_fin: b.hora_fin, aula: b.aula || "" })} />
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="modal-horario">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar bloque" : "Agregar clase al horario"}</DialogTitle>
            <DialogDescription>
              El sistema avisa si el grado, el docente o el aula ya están ocupados en ese horario.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Materia</Label>
                <select value={form.materia_id} data-testid="horario-materia"
                        onChange={(e) => setForm({ ...form, materia_id: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <option value="">Seleccione…</option>
                  {materias.map((m) => (
                    <option key={m.id} value={m.id}>
                      {`${m.nombre} — ${m.grado}${m.docente ? ` (${m.docente})` : ""}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Día</Label>
                  <select value={form.dia} data-testid="horario-dia"
                          onChange={(e) => setForm({ ...form, dia: Number(e.target.value) })}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                    {DIAS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Desde</Label>
                  <Input type="time" value={form.hora_inicio} data-testid="horario-inicio"
                         onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Hasta</Label>
                  <Input type="time" value={form.hora_fin} data-testid="horario-fin"
                         onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Aula o salón (opcional)</Label>
                <Input value={form.aula} placeholder="Aula 3-B" data-testid="horario-aula"
                       onChange={(e) => setForm({ ...form, aula: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 rounded-full py-6" onClick={guardar} data-testid="guardar-horario">
                  <Save className="mr-2 h-4 w-4" /> {form.id ? "Guardar cambios" : "Agregar al horario"}
                </Button>
                {form.id && (
                  <Button variant="outline" className="rounded-full py-6" onClick={eliminar}
                          data-testid="eliminar-horario">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
